import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useQuery } from '@tanstack/react-query'
import { printFormat } from 'iban-ts'
import { useForm } from 'react-hook-form'

import { getCountries } from '../../../../api/countries/get-countries'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  CurrencyEnum,
  currencyEnumMapping
} from '../../../../lib/types/enums/common/currency.enum'
import { OlBankAccountInput } from '../../../../lib/types/models/ol/ol-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'
import { OL_BANK_ACCOUNT_FORM_SCHEMA_VALIDATION } from '../utils'

type Props = BaseFormProps<OlBankAccountInput, true> & {
  className?: string
}

const OlBankAccountForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const { currentLanguage } = useTranslate()

  const form = useForm<OlBankAccountInput>({
    resolver: zodResolver(OL_BANK_ACCOUNT_FORM_SCHEMA_VALIDATION(currentLanguage)),
    defaultValues,
    mode: FORM_MODE
  })
  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-5', className)}>
          <FormField
            control={form.control}
            name="bankAccountHolder"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type="text"
                    label="Titulaire du compte bancaire"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledSelectInput<CurrencyEnum>
                    {...field}
                    required
                    options={Object.values(CurrencyEnum).map((currency) => ({
                      label: currencyEnumMapping[currency].label,
                      value: currency
                    }))}
                    label={'Devise'}
                    selectedOption={getSelectedEnumOption(field.value, currencyEnumMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex gap-2">
            <FormField
              control={form.control}
              name="iban"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput
                      {...field}
                      required
                      type="text"
                      label="IBAN"
                      placeholder={`ex: ${printFormat('FR1234567891011121314151617')}`}
                      onChange={(e) => {
                        const value = e ? (typeof e === 'string' ? e : e.target.value) : ''
                        form.setValue('iban', printFormat(value), { shouldValidate: true })
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bic"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} required type="text" label="BIC" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="hostCountries"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    required
                    label="Pays de séjour"
                    variant="primary"
                    dropDownPosition="above"
                    options={
                      countries?.data.map((country) => ({
                        label: country.name,
                        value: country.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((country) => ({
                        label: country.name,
                        value: country.id
                      })) ?? []
                    }
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map((option) => ({
                          id: option,
                          name: countries?.data.find((country) => country.id === option)?.name
                        }))
                      )
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default OlBankAccountForm
